import { ApolloClient, createHttpLink, from } from '@apollo/client/core';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import { InMemoryCache } from '@apollo/client/cache';
import { createApolloProvider } from '@vue/apollo-option';
import { readMeta } from './page';
import { refreshedJwt } from './jwt';

export let sharedApolloProvider: ReturnType<typeof createApolloProvider> | null = null;

export function getSharedApolloClient() {
  if (!sharedApolloProvider) {
    throw new Error('Apollo provider not initialized. Call setupApollo() first.');
  }

  return sharedApolloProvider.defaultClient;
}

export function setupApollo(includeJWT = true, defaultOptions = {} as Object) {
  // Apollo/Graph QL setup
  const gqlURI = readMeta('graphql');
  if (!gqlURI) {
    throw new Error('GQL endpoint not specified');
  }

  const httpLink = createHttpLink({ uri: gqlURI });

  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: 5000,
      jitter: true,
    },
    attempts: {
      max: 3,
      retryIf: (error) => !!error,
    },
  });

  const authLink = setContext(async () => {
    // don't include the jwt if the call doesn't need it
    if (!includeJWT) return {};

    const token = await refreshedJwt();

    // don't include the jwt if there was no jwt to include
    if (!token) return {};

    // finally, if the token needs refreshing, do that and then return the new bearer token header
    return { headers: { authorization: `Bearer ${token}` } };
  });

  const additiveLinks = from([authLink, retryLink, httpLink]);

  const client = new ApolloClient({
    link: additiveLinks,
    cache: new InMemoryCache(),
    defaultOptions,
  });

  // Create and return the Apollo provider using the Vue 3 compatible API
  sharedApolloProvider = createApolloProvider({
    defaultClient: client,
  });

  return sharedApolloProvider;
}

export default {
  setupApollo,
};
